import {
    useCallback,
    useEffect,
    useState,
    useRef
} from "react";
import type {
    ChangeEvent
} from "react";
import {
    useWindowDimensions,
    ActivityIndicator,
    TouchableOpacity,
    ScrollView,
    Platform,
    Linking,
    Image,
    Modal,
    View
} from "react-native";
import type IDriveSelectorModalProps from "./type";
import type {
    DriveFolderPayloadType,
    DriveFilePayloadType
} from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitSnackBar,
    NCoreUIKitTheme,
    TextInput,
    Dialog,
    Button,
    Text
} from "ncore-ui-kit";
import {
    FileSpreadsheetIcon,
    FileArchiveIcon,
    FolderPlusIcon,
    ArrowLeftIcon,
    FileAudioIcon,
    FileImageIcon,
    FileVideoIcon,
    FileTextIcon,
    FileCodeIcon,
    UploadIcon,
    FolderIcon,
    FileIcon,
    XIcon
} from "lucide-react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import {
    SERVER_ADDRESS
} from "../../services/restAPI/constants";
import RESTServiceContext from "../../services/restAPI/context";

const DriveSelectorModal = ({
    isVisible,
    onSelect,
    onClose
}: IDriveSelectorModalProps) => {
    const {
        height,
        width
    } = useWindowDimensions();
    const isMobile = width < 768;

    const [
        currentFolderID,
        setCurrentFolderID
    ] = useState<string | undefined>(undefined);

    const [
        folderHistory,
        setFolderHistory
    ] = useState<{ id: string | undefined; name: string }[]>([
        {
            name: "Dosyalar",
            id: undefined
        }
    ]);

    const [
        folders,
        setFolders
    ] = useState<DriveFolderPayloadType[]>([]);

    const [
        files,
        setFiles
    ] = useState<DriveFilePayloadType[]>([]);

    const [
        loading,
        setLoading
    ] = useState<boolean>(true);

    const [
        isCreateFolderModalOpen,
        setIsCreateFolderModalOpen
    ] = useState<boolean>(false);

    const [
        newFolderName,
        setNewFolderName
    ] = useState<string>("");

    const [
        isUploading,
        setIsUploading
    ] = useState<boolean>(false);

    const [
        isPreviewModalOpen,
        setIsPreviewModalOpen
    ] = useState<boolean>(false);

    const [
        previewUrl,
        setPreviewUrl
    ] = useState<string>("");

    const [
        authToken,
        setAuthToken
    ] = useState<string>("");

    const fileInputRef = useRef<HTMLInputElement>(null);

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const {
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const fetchDriveItems = useCallback(async (folderID: string | undefined) => {
        if(!isVisible) return;

        try {
            setLoading(true);

            const folderParams: { parentFolderID?: string } = {};
            if(folderID) {
                folderParams.parentFolderID = folderID;
            }
            const foldersResult = await RESTServiceContext.action("getAllDriveFolders", folderParams);

            const fileParams: { folderID?: string } = {};
            if(folderID) {
                fileParams.folderID = folderID;
            }
            const filesResult = await RESTServiceContext.action("getAllDriveFiles", fileParams);

            if(foldersResult.data) {
                setFolders(foldersResult.data as unknown as DriveFolderPayloadType[]);
            }

            if(filesResult.data) {
                if(filesResult.data.data) {
                    setFiles(filesResult.data.data);
                } else {
                    setFiles(filesResult.data as unknown as DriveFilePayloadType[]);
                }
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, [isVisible]);

    useEffect(() => {
        if (isVisible) {
            fetchDriveItems(currentFolderID);
            AsyncStorage.getItem("authToken").then((token: string | null) => {
                if(token) {
                    setAuthToken(token);
                }
            });
        }
    }, [
        currentFolderID,
        fetchDriveItems,
        isVisible
    ]);

    const handleFileUpload = async (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if(!file) return;

        try {
            setIsUploading(true);
            const tokenResult = await RESTServiceContext.action("getUploadTokenForDrive", {
                fileType: file.type || "application/octet-stream",
                folderID: currentFolderID || undefined,
                fileName: file.name,
                fileSize: file.size,
                isPublic: true
            });

            const payload = tokenResult as unknown as { payload?: { token?: string; uploadToken?: string } };
            const token = payload.payload?.token || payload.payload?.uploadToken;

            if (token) {
                const formData = new FormData();
                formData.append("file", file);
                await fetch(`${process.env.STORAGE_URL}/upload`, {
                    headers: {
                        Authorization: token
                    },
                    body: formData,
                    method: "POST"
                });
                await new Promise(resolve => setTimeout(resolve, 2000));
                await fetchDriveItems(currentFolderID);
                NCoreUIKitSnackBar.open({
                    title: localize("photo-uploaded") || "Dosya başarıyla yüklendi."
                });
            }
        } catch (error) {
            console.error(error);
            NCoreUIKitSnackBar.open({
                title: localize("photo-upload-error") || "Dosya yüklenirken hata oluştu."
            });
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        }
    };

    const handleCreateFolder = async () => {
        if(!newFolderName.trim()) return;
        try {
            await RESTServiceContext.action("createDriveFolder", {
                parentFolderID: currentFolderID || undefined,
                name: newFolderName,
                isPublic: true
            });
            setNewFolderName("");
            setIsCreateFolderModalOpen(false);
            await fetchDriveItems(currentFolderID);
            NCoreUIKitSnackBar.open({
                title: localize("drive-folder-created-success") || "Klasör başarıyla oluşturuldu."
            });
        } catch(error) {
            console.error(error);
            NCoreUIKitSnackBar.open({
                title: localize("drive-folder-created-error") || "Klasör oluşturulurken hata oluştu."
            });
        }
    };

    const handleNavigateFolder = (folderID: string, name: string) => {
        setFolderHistory(prev => [
            ...prev,
            {
                id: folderID,
                name
            }
        ]);
        setCurrentFolderID(folderID);
    };

    const handleGoBack = () => {
        if(folderHistory.length > 1) {
            const newHistory = [...folderHistory];
            newHistory.pop();
            setFolderHistory(newHistory);
            if (newHistory.length > 0) {
                const lastItem = newHistory[newHistory.length - 1];
                if (lastItem) {
                    setCurrentFolderID(lastItem.id);
                }
            }
        }
    };

    const renderHeader = () => {
        const lastFolder = folderHistory[folderHistory.length - 1];
        const currentFolderName = lastFolder ? lastFolder.name : "Dosyalar";

        return <View
            style={[
                stylesheet.headerContainer,
                {
                    marginBottom: spaces.spacingLg
                }
            ]}
        >
            <View
                style={[
                    stylesheet.headerTitleContainer,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                {
                    folderHistory.length > 1 ?
                        <Button
                            icon={() => <ArrowLeftIcon
                                color={colors.project.brandWhite}
                                size={24}
                            />}
                            customBorderColor="secondaryButton"
                            customColor="secondaryButton"
                            spreadBehaviour="free"
                            onPress={handleGoBack}
                            variant="filled"
                        />
                    :
                        null
                }
                <Text
                    variant="titleLargeSize"
                    numberOfLines={1}
                >
                    {currentFolderName}
                </Text>
            </View>
            <View
                style={[
                    stylesheet.headerActions,
                    {
                        gap: spaces.spacingSm
                    }
                ]}
            >
                <Button
                    title={localize("drive-upload-file-button") || "Dosya Yükle"}
                    onPress={() => fileInputRef.current?.click()}
                    customTextColor={colors.project.brandWhite}
                    icon={({
                        size
                    }) => <UploadIcon
                        color={colors.project.brandWhite}
                        size={size}
                    />}
                    customBorderColor="secondaryButton"
                    customColor="secondaryButton"
                    isLoading={isUploading}
                    spreadBehaviour="free"
                    variant="outline"
                />
                <Button
                    title={localize("drive-create-folder-button") || "Yeni Klasör"}
                    onPress={() => setIsCreateFolderModalOpen(true)}
                    customTextColor={colors.project.brandWhite}
                    icon={({
                        color,
                        size
                    }) => <FolderPlusIcon
                        color={colors.content.icon[color]}
                        size={size}
                    />}
                    spreadBehaviour="free"
                    customColor="gold"
                />
                <TouchableOpacity
                    style={stylesheet.closeButton}
                    activeOpacity={0.7}
                    onPress={onClose}
                >
                    <XIcon
                        color={colors.content.icon.high}
                        size={24}
                    />
                </TouchableOpacity>
            </View>
        </View>;
    };

    const renderFolder = (folder: DriveFolderPayloadType, index: number) => {
        const cardWidth = isMobile ? "48%" : 200;
        return <View
            style={[
                stylesheet.itemWrapper,
                {
                    width: cardWidth as unknown as number
                }
            ]}
            key={folder._id || `folder-${index}`}
        >
            <TouchableOpacity
                onPress={() => handleNavigateFolder(folder._id, folder.name)}
                style={[
                    stylesheet.itemContainer,
                    {
                        backgroundColor: colors.project.brandWhite,
                        borderColor: colors.project.brandBorder,
                        borderWidth: borders.seperator,
                        borderRadius: radiuses.md,
                        padding: spaces.spacingXs,
                        gap: spaces.spacingSm
                    }
                ]}
                activeOpacity={0.7}
            >
                <FolderIcon
                    color={colors.content.icon.mid}
                    size={100}
                />
                <Text
                    style={[
                        stylesheet.itemTitle,
                        {
                            marginTop: spaces.spacingSm
                        }
                    ]}
                    variant="bodyMediumSize"
                    numberOfLines={1}
                >
                    {folder.name}
                </Text>
            </TouchableOpacity>
        </View>;
    };

    const renderFile = (file: DriveFilePayloadType, index: number) => {
        const cardWidth = isMobile ? "48%" : 200;
        const isImage = file.fileType.startsWith("image/");
        const parsedFilePath = file.filePath.startsWith("/") ? `${SERVER_ADDRESS}${file.filePath}` : file.filePath;
        const encodedFilePath = encodeURI(parsedFilePath);
        const tokenQuery = encodedFilePath.includes("?") ? `&token=${authToken}` : `?token=${authToken}`;
        const fileUrl = file.isPublic ? encodedFilePath : `${encodedFilePath}${tokenQuery}`;

        let FileIconComponent = FileIcon;
        const ft = file.fileType.toLowerCase();

        if (ft.includes("pdf")) {
            FileIconComponent = FileTextIcon;
        } else if (ft.includes("sheet") || ft.includes("excel") || ft.includes("csv")) {
            FileIconComponent = FileSpreadsheetIcon;
        } else if (ft.includes("video")) {
            FileIconComponent = FileVideoIcon;
        } else if (ft.includes("audio")) {
            FileIconComponent = FileAudioIcon;
        } else if (ft.includes("zip") || ft.includes("rar") || ft.includes("archive") || ft.includes("tar")) {
            FileIconComponent = FileArchiveIcon;
        } else if (ft.includes("text") || ft.includes("document") || ft.includes("word")) {
            FileIconComponent = FileTextIcon;
        } else if (ft.includes("json") || ft.includes("xml") || ft.includes("html") || ft.includes("javascript")) {
            FileIconComponent = FileCodeIcon;
        } else if (ft.includes("image")) {
            FileIconComponent = FileImageIcon;
        }

        return <View
            style={[
                stylesheet.itemWrapper,
                {
                    width: cardWidth as unknown as number
                }
            ]}
            key={file._id || `file-${index}`}
        >
            <TouchableOpacity
                style={[
                    stylesheet.itemContainer,
                    {
                        backgroundColor: colors.project.brandWhite,
                        borderColor: colors.project.brandBorder,
                        borderWidth: borders.seperator,
                        borderRadius: radiuses.md,
                        padding: spaces.spacingXs,
                        gap: spaces.spacingSm
                    }
                ]}
                onPress={() => onSelect(fileUrl, file)}
                onLongPress={() => {
                    if (isImage) {
                        setPreviewUrl(fileUrl);
                        setIsPreviewModalOpen(true);
                    } else {
                        Linking.openURL(fileUrl);
                    }
                }}
                activeOpacity={0.7}
            >
                {
                    isImage ?
                        <Image
                            style={[
                                stylesheet.imagePreview,
                                {
                                    borderRadius: radiuses.md
                                }
                            ]}
                            resizeMode="cover"
                            source={{
                                uri: fileUrl
                            }}
                        />
                    :
                        <FileIconComponent
                            color={colors.content.icon.mid}
                            size={100}
                        />
                }
                <Text
                    style={[
                        stylesheet.itemTitle,
                        {
                            marginTop: spaces.spacingSm
                        }
                    ]}
                    variant="bodyMediumSize"
                    numberOfLines={1}
                >
                    {file.fileName}
                </Text>
            </TouchableOpacity>
        </View>;
    };

    const renderContent = () => {
        if(loading) {
            return <View
                style={stylesheet.emptyContainer}
            >
                <ActivityIndicator
                    color={colors.project.gold}
                    size="large"
                />
            </View>;
        }

        if(folders.length === 0 && files.length === 0) {
            return <View
                style={stylesheet.emptyContainer}
            >
                <Text
                    style={[
                        stylesheet.emptyText,
                        {
                            color: colors.content.text.mid
                        }
                    ]}
                    variant="bodyLargeSize"
                >
                    {localize("drive-empty-folder")}
                </Text>
            </View>;
        }

        return <View
            style={[
                stylesheet.gridContainer,
                {
                    gap: spaces.spacingSm
                }
            ]}
        >
            {folders.map(renderFolder)}
            {files.map(renderFile)}
        </View>;
    };

    const renderCreateFolderModal = () => {
        return <Dialog
            title={localize("drive-create-folder-button") || "Yeni Klasör"}
            contentContainerStyle={stylesheet.dialogContentContainer}
            secondaryButtonProps={{
                customTextColor: colors.project.brandWhite,
                onPress: () => {
                    setIsCreateFolderModalOpen(false);
                    setNewFolderName("");
                },
                customBorderColor: "secondaryButton",
                title: localize("cancel") || "İptal",
                customColor: "secondaryButton",
                variant: "filled"
            }}
            primaryButtonProps={{
                title: localize("save") || "Kaydet",
                onPress: handleCreateFolder
            }}
            onClosed={() => {
                setIsCreateFolderModalOpen(false);
                setNewFolderName("");
            }}
            onOverlayPress={() => {
                setIsCreateFolderModalOpen(false);
                setNewFolderName("");
            }}
            isVisible={isCreateFolderModalOpen}
            variant="yes-no"
        >
            <View>
                <TextInput
                    placeholder={localize("drive-folder-name-placeholder") || "Klasör adı"}
                    customColor={colors.project.inputBackground}
                    onChangeText={setNewFolderName}
                    spreadBehaviour="stretch"
                    value={newFolderName}
                />
            </View>
        </Dialog>;
    };

    return <Dialog
        bottomComponent={
            <View
                style={[
                    stylesheet.modalBottomContainer,
                    {
                        paddingHorizontal: spaces.spacingLg,
                        paddingBottom: spaces.spacingLg
                    }
                ]}
            >
                <Button
                    title={localize("close-button") || "Kapat"}
                    customTextColor={colors.project.brandWhite}
                    customBorderColor="secondaryButton"
                    customColor="secondaryButton"
                    spreadBehaviour="stretch"
                    onPress={onClose}
                    variant="filled"
                />
            </View>
        }
        style={stylesheet.dialogContentContainer}
        contentContainerStyle={{
            minWidth: isMobile ? "100%" : 800
        }}
        contentJustify="language-based"
        onOverlayPress={onClose}
        isVisible={isVisible}
        onClosed={onClose}
    >
        <View
            style={[
                stylesheet.contentContainer,
                {
                    maxHeight: Math.max(Math.min(height - 240, 580), 300)
                }
            ]}
        >
            {Platform.OS === "web" && (<input
                onChange={handleFileUpload}
                style={{
                    display: "none"
                }}
                ref={fileInputRef}
                type="file"
            />)}
            {renderCreateFolderModal()}
            <Modal
                onRequestClose={() => setIsPreviewModalOpen(false)}
                visible={isPreviewModalOpen}
                animationType="fade"
                transparent={true}
            >
                <View
                    style={[
                        stylesheet.modalOverlay,
                        {
                            backgroundColor: colors.content.container.overlay
                        }
                    ]}
                >
                    <TouchableOpacity
                        onPress={() => setIsPreviewModalOpen(false)}
                        style={stylesheet.lightboxCloseButton}
                    >
                        <XIcon
                            color={colors.project.brandWhite}
                            size={32}
                        />
                    </TouchableOpacity>
                    {
                        previewUrl ?
                            <Image
                                style={stylesheet.lightboxImage}
                                resizeMode="contain"
                                source={{
                                    uri: previewUrl
                                }}
                            />
                        :
                            null
                    }
                </View>
            </Modal>
            {renderHeader()}
            <ScrollView
                showsVerticalScrollIndicator={false}
                style={stylesheet.scrollContainer}
            >
                {renderContent()}
            </ScrollView>
        </View>
    </Dialog>;
};

export default DriveSelectorModal;
